import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { MatchState, ValueStore } from "../store/constants";
import { LeagueMatchSmallElement, MatchSmallElement } from "../config/types";
import { MatchLeague, TeamOfTheWeekLineup } from "../client/api";

export const selectLeagueMatches = (
  s: RootState
): ReadonlyArray<LeagueMatchSmallElement> =>
  s.match.leagueMatches?.response ?? [];

export const selectMatchById = (matchId: number | string) =>
  createSelector(
    [selectLeagueMatches],
    (groups): MatchSmallElement | undefined => {
      for (const group of groups) {
        const found = group.matches.find(
          (m) => String(m.id) === String(matchId)
        );
        if (found) return found;
      }
      return undefined;
    }
  );

export const selectTeamMatches = (teamId: number | string) =>
  createSelector([selectLeagueMatches], (groups): MatchSmallElement[] => {
    const normalizedTeamId = Number(teamId);
    if (!Number.isFinite(normalizedTeamId)) {
      return [];
    }
    const teamGroup = groups.find(
      (g) => g.teamId === normalizedTeamId
    );
    return teamGroup?.matches ?? [];
  });

export const selectMatchSlice = (s: RootState): MatchState => s.match;

export const selectMatchStatus = createSelector(
  selectMatchSlice,
  (m) => m.status
);

export const selectMatchDays = createSelector(
  selectMatchSlice,
  (m) => m.matchDays
);

export const selectLeagueMatchDays = createSelector(
  selectMatchSlice,
  (m) => m.leagueMatchDays
);

export const selectMatchElements = createSelector(
  selectMatchSlice,
  (m) => m.matchElements
);

export const makeSelectTotwByLeagueAndDay = (
  leagueId: number | string,
  matchDay: number
) =>
  createSelector(
    selectMatchSlice,
    (m: MatchState): (TeamOfTheWeekLineup | null) | undefined => {
      const list = m.totw ?? [];
      return list.find(
        (v) => String(v.id) === String(leagueId) && v.date === matchDay
      )?.response;
    }
  );

export const makeSelectLeagueMatchDaysById = (leagueId: number | string) =>
  createSelector(
    selectLeagueMatchDays,
    (list: ReadonlyArray<ValueStore<Date[]>>) =>
      list.find((x) => String(x.id) === String(leagueId))?.response ?? []
  );
export const selectLeagueMatchesContainer = (
  s: RootState
): ValueStore<LeagueMatchSmallElement[]> | undefined => s.match.leagueMatches;

export const makeSelectFullMatchById =
  (matchId: number | string) =>
  (s: RootState): MatchLeague | undefined =>
    s.match.matches.find((m) => String(m.id) === String(matchId));

export const selectMatchScreenById = (matchId: number) => (s: RootState) =>
  s.match.matchScreens?.[matchId] ?? null;

export const selectIsMatchScreenLoaded = (matchId: number) => (s: RootState) =>
  !!s.match.matchScreens?.[matchId];
